import type { DashboardFilters } from '@delivery-metrics/shared';
import {
  CloudOff,
  Assessment,
  Error as ErrorIcon,
  Refresh,
  Speed,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Button,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

import { FilterPanel, type FilterConfig } from '../components/FilterPanel';
import { CollectorStatsDialog } from '../components/CollectorStatsDialog';
import { FreshnessIndicator } from '../components/FreshnessIndicator';
import { EIRnDTopLevelKPISection } from '../components/leadership/EIRnDTopLevelKPISection';
import { useData } from '../contexts/DataContext';
import { useDashboardComposite } from '../hooks/useDashboardComposite';
import { useDashboardFreshness } from '../hooks/useDashboardFreshness';
import { useDashboardFilters } from '../hooks/useDashboardFilters';
import {
  scopedDataSources,
  scopedPrograms,
  unitProgramMapByLabel,
} from '../utils/filterScope';

type DataSourceOptionsLike = {
  repositories?: string[];
  areaPaths?: string[];
  sonarqubeProjects?: string[];
};

// Case-insensitive prune mirroring the backend filter normalisation; the
// original selection value is preserved in the result.
const pruneToScope = (selected: string[] | undefined, available?: string[]) => {
  const allowed = new Set((available || []).map(value => value.toLowerCase()));
  return (selected || []).filter(value => allowed.has(value.toLowerCase()));
};

// Returns filters with data-source selections pruned to the scoped options, or
// the same reference when nothing changed (so callers can skip a state update).
const pruneFilterDataSources = <T extends DashboardFilters>(
  filters: T,
  ds: DataSourceOptionsLike
): T => {
  const repositories = pruneToScope(filters.repositories, ds.repositories);
  const areaPaths = pruneToScope(filters.areaPaths, ds.areaPaths);
  const sonarqubeProjects = pruneToScope(
    filters.sonarqubeProjects,
    ds.sonarqubeProjects
  );
  if (
    repositories.length === (filters.repositories?.length ?? 0) &&
    areaPaths.length === (filters.areaPaths?.length ?? 0) &&
    sonarqubeProjects.length === (filters.sonarqubeProjects?.length ?? 0)
  ) {
    return filters;
  }
  return { ...filters, repositories, areaPaths, sonarqubeProjects };
};

export const EIRnDTopLevelKPIDashboard: React.FC = () => {
  const { filters, updateFilters, resetFilters } = useDashboardFilters();
  const [draftFilters, setDraftFilters] = useState<DashboardFilters>(filters);
  const [isCollectorStatsOpen, setCollectorStatsOpen] = useState(false);

  // Sync draft filters when applied filters change (e.g. after reset)
  useEffect(() => {
    setDraftFilters(filters);
  }, [filters]);

  const hasPendingChanges =
    JSON.stringify(draftFilters) !== JSON.stringify(filters);
  const { isRefreshing, hasErrors, clearErrors } = useData();

  // Mock connection status for now
  const connectionStatus = {
    isOnline: true,
    isRealTimeConnected: true,
    realTimeError: null,
    hasOfflineData: false,
    isSyncing: false,
  };

  // Freshness and every dropdown come from the one bootstrap request
  // (US-23, US-22). The filter options carry the scope they were observed in,
  // so narrowing them to the current selection is local arithmetic rather
  // than a request per change.
  const {
    metadata: freshness,
    filterOptions: bootstrapOptions,
    isLoading: freshnessLoading,
    refetch: refetchFreshness,
  } = useDashboardFreshness();

  // The five KPI blocks, from one request, for the *applied* filters.
  //
  // This replaced `useLeadershipMetrics` here as well as the section's six
  // calls. That query was left only to decide whether to render — a whole v1
  // request per view whose payload nothing displayed — and while it remained,
  // the page could not honestly claim two requests.
  const {
    leadershipKPIs,
    metricsData,
    predictabilityPoints,
    isLoading,
    isStaleView,
    error,
    refetch: refetchComposite,
  } = useDashboardComposite(filters);

  const refresh = {
    leadership: refetchComposite,
  };

  // Unit display value -> programme display values. The bootstrap options
  // carry canonical scope keys; this is where the two spellings are
  // reconciled, once.
  const unitProgramMap = useMemo(
    () => unitProgramMapByLabel(bootstrapOptions),
    [bootstrapOptions]
  );

  const availablePrograms = useMemo(() => {
    const allPrograms = (bootstrapOptions?.program ?? []).map(
      option => option.label
    );
    const selectedUnits = draftFilters.units || [];

    if (!selectedUnits.length) {
      return allPrograms;
    }

    // Scoped by the options' own `units`, which is the same answer the
    // unit-to-programme map gives — one source, not two that can disagree.
    const scoped = scopedPrograms(bootstrapOptions, selectedUnits);
    return scoped.length ? scoped : allPrograms;
  }, [draftFilters.units, bootstrapOptions]);

  // Data-source options for the *draft* scope, so the dropdowns react to a
  // selection before it is applied — the behaviour the scoped request had.
  const dataSources = useMemo(
    () =>
      scopedDataSources(bootstrapOptions, {
        units: draftFilters.units,
        programs: draftFilters.programs,
      }),
    [bootstrapOptions, draftFilters.units, draftFilters.programs]
  );

  const filterOptions = useMemo(() => {
    const toOptions = (values?: string[]) =>
      (values || []).map(value => ({ value, label: value }));
    return {
      units: (bootstrapOptions?.unit ?? []).map(option => ({
        value: option.label,
        label: option.label,
      })),
      programs: availablePrograms.map(program => ({
        value: program,
        label: program,
      })),
      repositories: toOptions(dataSources.repositories),
      areaPaths: toOptions(dataSources.areaPaths),
      sonarqubeProjects: toOptions(dataSources.sonarqubeProjects),
    };
  }, [bootstrapOptions, availablePrograms, dataSources]);

  const applyFilters = (nextFilters: DashboardFilters) => {
    const prevUnits = (filters.units || []).slice().sort();
    const nextUnits = (nextFilters.units || []).slice().sort();
    const unitsChanged =
      JSON.stringify(prevUnits) !== JSON.stringify(nextUnits);

    // Resolve the program scope that will actually be applied (units may
    // auto-select / constrain programs).
    let resolved = nextFilters;
    if (unitsChanged) {
      const scopedPrograms = nextUnits.length
        ? Array.from(
            new Set(nextUnits.flatMap(unit => unitProgramMap[unit] || []))
          )
        : [];
      // Preserve the user's manual program selection when it is a valid
      // subset of the category-scoped programs; only auto-expand to all
      // scoped programs when the user hasn't narrowed the list.
      const userPrograms = (nextFilters.programs || []).filter(p =>
        scopedPrograms.includes(p)
      );
      resolved = {
        ...nextFilters,
        programs: userPrograms.length ? userPrograms : scopedPrograms,
      };
    } else if (
      (nextFilters.units || []).length &&
      (nextFilters.programs || []).length
    ) {
      const allowedPrograms = new Set(
        (nextFilters.units || []).flatMap(unit => unitProgramMap[unit] || [])
      );
      const constrainedPrograms = (nextFilters.programs || []).filter(program =>
        allowedPrograms.has(program)
      );

      if (constrainedPrograms.length !== (nextFilters.programs || []).length) {
        resolved = { ...nextFilters, programs: constrainedPrograms };
      }
    }

    // Still-valid data-source selections must survive Apply; only now-invalid
    // ones are dropped.
    //
    // US-22 made this unconditional. The options for *any* scope are now
    // derived locally from the bootstrap response, so they can be computed
    // for the scope actually being applied — `resolved`, which may differ
    // from the draft. The previous version had to skip pruning whenever the
    // scoped request was in flight or keyed to a different scope, and left an
    // effect to correct the filters afterwards; that window is gone, and with
    // it the possibility of the KPI request running once with a now-invalid
    // value.
    updateFilters(
      pruneFilterDataSources(
        resolved,
        scopedDataSources(bootstrapOptions, {
          units: resolved.units,
          programs: resolved.programs,
        })
      )
    );
  };

  // Data-source filters are per-team drill-downs: only shown once a Product
  // Team (program) is selected, and their options are scoped to that team.
  const hasTeamSelected = (draftFilters.programs?.length ?? 0) > 0;

  // Clear any data-source selections when no team is selected, so hidden
  // filters can never silently narrow the KPIs.
  const handleDraftChange = (next: DashboardFilters) => {
    if (!next.programs?.length) {
      setDraftFilters({
        ...next,
        repositories: [],
        areaPaths: [],
        sonarqubeProjects: [],
      });
      return;
    }
    setDraftFilters(next);
  };

  // Prune data-source selections that are no longer valid for the current
  // scope. A selection belonging to a previously selected team would
  // otherwise linger in draftFilters — invisible in the UI (absent from the
  // narrowed options) but still applied to the KPI request, silently
  // narrowing the figures. Only values absent from the scoped options are
  // dropped, so still-valid picks survive.
  //
  // This still runs on an effect, but for one reason only: the bootstrap
  // response arrives after the first render, and a selection restored from
  // the URL cannot be checked until it does.
  useEffect(() => {
    if (!bootstrapOptions) {
      return;
    }

    setDraftFilters(prev =>
      pruneFilterDataSources(
        prev,
        scopedDataSources(bootstrapOptions, {
          units: prev.units,
          programs: prev.programs,
        })
      )
    );

    const pruned = pruneFilterDataSources(
      filters,
      scopedDataSources(bootstrapOptions, {
        units: filters.units,
        programs: filters.programs,
      })
    );
    if (pruned !== filters) {
      updateFilters({
        repositories: pruned.repositories,
        areaPaths: pruned.areaPaths,
        sonarqubeProjects: pruned.sonarqubeProjects,
      });
    }
  }, [bootstrapOptions, filters, updateFilters]);

  const filterConfigs: FilterConfig[] = [
    {
      key: 'timeRange',
      label: 'Time Period',
      type: 'daterange',
      required: true,
      width: 'large',
    },
    {
      key: 'units',
      label: 'Categories',
      type: 'multiselect',
      options: filterOptions.units,
      width: 'medium',
    },
    {
      key: 'programs',
      label: 'Product Teams',
      type: 'multiselect',
      options: filterOptions.programs,
      width: 'medium',
    },
    ...(hasTeamSelected
      ? ([
          {
            key: 'repositories',
            label: 'Repositories',
            type: 'multiselect',
            options: filterOptions.repositories,
            width: 'medium',
            group: 'dataSources',
          },
          {
            key: 'areaPaths',
            label: 'Areas',
            type: 'multiselect',
            options: filterOptions.areaPaths,
            width: 'medium',
            group: 'dataSources',
          },
          {
            key: 'sonarqubeProjects',
            label: 'SonarQube Projects',
            type: 'multiselect',
            options: filterOptions.sonarqubeProjects,
            width: 'medium',
            group: 'dataSources',
          },
        ] as FilterConfig[])
      : []),
  ];

  // Handle loading state
  if (isLoading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Speed sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant='h4' component='h1'>
            EI Innovation Effectiveness Top Level KPI Dashboard
          </Typography>
          <CircularProgress size={24} sx={{ ml: 2 }} />
        </Box>
      </Box>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Speed sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant='h4' component='h1'>
            EI Innovation Effectiveness Top Level KPI Dashboard
          </Typography>
        </Box>

        <Alert
          severity='error'
          action={
            <IconButton
              color='inherit'
              size='small'
              onClick={() => refetchComposite()}
            >
              <Refresh />
            </IconButton>
          }
        >
          Failed to load dashboard data: {error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with status indicators */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Speed sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant='h4' component='h1' sx={{ flexGrow: 1 }}>
          EI Innovation Effectiveness Top Level KPI Dashboard
        </Typography>

        {/* Connection status */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {!connectionStatus.isOnline && (
            <Chip
              icon={<CloudOff />}
              label='Offline'
              color='warning'
              size='small'
            />
          )}

          {hasErrors && (
            <Chip
              icon={<ErrorIcon />}
              label='Errors'
              color='error'
              size='small'
              onClick={clearErrors}
            />
          )}

          {/*
            `keepPreviousData` keeps the last successful view on screen while
            new filters load, which is what stops the page blanking out. Said
            out loud here, because figures that belong to the previous filters
            and figures that belong to the current ones look identical.
          */}
          {isStaleView && (
            <Chip
              icon={<CircularProgress size={12} color='inherit' />}
              label='Updating…'
              size='small'
              variant='outlined'
              data-testid='stale-view-indicator'
            />
          )}

          <FreshnessIndicator
            metadata={freshness}
            isLoading={freshnessLoading}
          />

          <IconButton
            onClick={() => {
              refresh.leadership();
              refetchFreshness();
            }}
            disabled={isRefreshing}
            size='small'
          >
            <Refresh />
          </IconButton>

          <Button
            variant='contained'
            size='small'
            startIcon={<Assessment />}
            onClick={() => setCollectorStatsOpen(true)}
          >
            collector stats
          </Button>
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <FilterPanel
          filters={draftFilters}
          onFiltersChange={handleDraftChange}
          onReset={resetFilters}
          onApply={() => applyFilters(draftFilters)}
          applyDisabled={!hasPendingChanges}
          filterConfigs={filterConfigs}
          showPresets={true}
          enableUrlSync={false}
          collapsible={true}
          defaultExpanded={false}
        />
      </Box>

      {/* EI Innovation Effectiveness Top Level KPIs Section */}
      {/*
        One loading prop, not two with the same value: `metricsLoading` is the
        one that stands down in mock-data mode, where there is nothing to wait
        for. The page has already returned a spinner by this point anyway.
      */}
      <EIRnDTopLevelKPISection
        filters={filters}
        leadershipKPIs={leadershipKPIs}
        metricsData={metricsData ?? undefined}
        predictabilityPoints={predictabilityPoints ?? undefined}
        metricsLoading={isLoading}
      />

      <CollectorStatsDialog
        open={isCollectorStatsOpen}
        onClose={() => setCollectorStatsOpen(false)}
      />
    </Box>
  );
};
